import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Plus, FileSpreadsheet, Phone, Copy, UserPlus } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AdminTitle, Spinner } from "@/admin/AdminLayout";
import { CB_STATUSES, Badge, Empty, copyText, download, useList } from "@/admin/hr/common";
import { NotesPanel, ReminderPanel, MailPanel, DeletePanel } from "@/admin/hr/Panels";

const NEW = { name: "", phone: "", email: "", interest: "İş başvurusu", preferred_time: "", note: "", status: "Geri Aranacak" };

function AddDialog({ open, onClose, onAdded }) {
  const [v, setV] = useState(NEW);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/hr/callbacks", { ...v, email: v.email || null });
      toast.success("Kayıt eklendi");
      setV(NEW);
      onAdded();
      onClose();
    } catch (ex) {
      toast.error(errMsg(ex));
    }
  };
  const inp = (k, l, p = {}) => <label className="text-sm"><span className="field-label">{l}</span><input value={v[k]} onChange={set(k)} className="field-input" data-testid={`cb-new-${k}`} {...p} /></label>;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" data-testid="callback-add-dialog">
        <DialogHeader><DialogTitle>Telefonla arayan kişiyi kaydet</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
          {inp("name", "Ad Soyad *", { required: true })}
          {inp("phone", "Telefon *", { required: true, type: "tel" })}
          {inp("email", "E-posta (isteğe bağlı)", { type: "email" })}
          {inp("interest", "İlgilenilen hizmet / iş başvurusu *", { required: true })}
          {inp("preferred_time", "Aranmak için uygun zaman")}
          <label className="text-sm"><span className="field-label">Görüşme durumu</span>
            <select value={v.status} onChange={set("status")} className="field-input" data-testid="cb-new-status">{CB_STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
          </label>
          <label className="text-sm sm:col-span-2"><span className="field-label">Görüşme notu</span><textarea rows={3} value={v.note} onChange={set("note")} className="field-input" data-testid="cb-new-note" /></label>
          <button className="btn-red sm:col-span-2" data-testid="cb-new-save">Kaydet</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Detail({ item, onChanged, onClose }) {
  const [c, setC] = useState(item);
  const saved = (d) => { setC(d); onChanged(); };
  const patch = async (body) => {
    try {
      saved((await api.patch(`/admin/hr/callbacks/${c.id}`, body)).data);
      toast.success("Kaydedildi");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const convert = async () => {
    try {
      const r = await api.post(`/admin/hr/callbacks/${c.id}/convert`);
      toast.success(`Başvuru oluşturuldu: ${r.data.app_no}`);
      saved((await api.get(`/admin/hr/callbacks/${c.id}`)).data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const reload = async () => saved((await api.get(`/admin/hr/callbacks/${c.id}`)).data);
  return (
    <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="callback-detail">
      <DialogHeader><DialogTitle>{c.name} <span className="ml-2 text-sm text-[color:var(--red)]">{c.ref_no}</span></DialogTitle></DialogHeader>
      <p className="text-xs text-zinc-500">Kayıt: {fmtDate(c.created_at)} · Kaynak: {c.source}</p>
      <div className="flex flex-wrap gap-2">
        <a href={`tel:${c.phone}`} className="btn-outline !py-2"><Phone className="h-4 w-4" /> {c.phone}</a>
        <button onClick={() => copyText(c.phone)} className="btn-outline !py-2" data-testid="cb-copy-phone"><Copy className="h-4 w-4" /> Kopyala</button>
        {!c.application_id && <button onClick={convert} className="btn-red !py-2" data-testid="cb-convert-btn"><UserPlus className="h-4 w-4" /> Başvuruya Dönüştür</button>}
      </div>
      <dl className="grid gap-3 rounded-lg border border-zinc-200 p-4 text-sm sm:grid-cols-2">
        {[["E-posta", c.email], ["İlgilenilen konu", c.interest], ["Görüşme notu", c.note]].map(([l, v]) => <div key={l}><dt className="text-xs font-bold uppercase tracking-wider text-zinc-500">{l}</dt><dd className="whitespace-pre-wrap">{v || "-"}</dd></div>)}
      </dl>
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="text-sm"><span className="field-label">Görüşme durumu</span>
          <select value={c.status} onChange={(e) => patch({ status: e.target.value })} className="field-input" data-testid="cb-status-select">{CB_STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
        </label>
        <label className="text-sm"><span className="field-label">Uygun zaman</span><input defaultValue={c.preferred_time} onBlur={(e) => e.target.value !== c.preferred_time && patch({ preferred_time: e.target.value })} className="field-input" data-testid="cb-preferred" /></label>
        <label className="text-sm"><span className="field-label">Son iletişim</span><input type="date" value={c.last_contact_at || ""} onChange={(e) => patch({ last_contact_at: e.target.value })} className="field-input" data-testid="cb-last-contact" /></label>
      </div>
      <NotesPanel coll="callbacks" item={c} onSaved={saved} />
      <ReminderPanel coll="callbacks" item={c} />
      <MailPanel coll="callbacks" item={c} onSent={reload} />
      <DeletePanel coll="callbacks" item={c} no={c.ref_no} onDeleted={() => { onClose(); onChanged(); }} />
    </DialogContent>
  );
}

const CB_HEAD = ["Kayıt No", "Tarih", "Ad Soyad", "Telefon", "Konu", "Uygun Zaman", "Kaynak", "Durum"];

function CallbackTable({ rows, onOpen }) {
  if (!rows) return <Spinner />;
  if (rows.length === 0) return <Empty testid="callbacks-empty" />;
  return (
    <div className="overflow-x-auto rounded-lg border border-zinc-200 bg-white">
      <table className="w-full text-sm" data-testid="callbacks-table">
        <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500"><tr>{CB_HEAD.map((h) => <th key={h} className="whitespace-nowrap px-3 py-3">{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-zinc-100">
          {rows.map((r) => (
            <tr key={r.id} onClick={() => onOpen(r)} className="cursor-pointer hover:bg-zinc-50" data-testid={`callback-row-${r.id}`}>
              <td className="whitespace-nowrap px-3 py-3 font-semibold text-[color:var(--red)]">{r.ref_no}</td>
              <td className="whitespace-nowrap px-3 py-3 text-zinc-500">{fmtDate(r.created_at)}</td>
              <td className="px-3 py-3 font-semibold">{r.name}</td>
              <td className="whitespace-nowrap px-3 py-3">{r.phone}</td>
              <td className="px-3 py-3 text-zinc-600">{r.interest}</td>
              <td className="px-3 py-3 text-zinc-600">{r.preferred_time || "-"}</td>
              <td className="px-3 py-3 text-xs text-zinc-500">{r.source}</td>
              <td className="px-3 py-3"><Badge value={r.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Callbacks() {
  const [f, setF] = useState({ q: "", status: "", date_from: "", date_to: "" });
  const [rows, load] = useList("/admin/hr/callbacks", f);
  const [open, setOpen] = useState(null);
  const [adding, setAdding] = useState(false);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const id = params.get("id");
    if (id) api.get(`/admin/hr/callbacks/${id}`).then((r) => setOpen(r.data)).catch(() => toast.error("Kayıt bulunamadı"));
  }, [params]);
  const close = () => { setOpen(null); if (params.get("id")) setParams({}); };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div>
      <AdminTitle title="Telefonla İletişim ve Geri Arama" desc={rows ? `${rows.length} kayıt` : ""}>
        <button onClick={() => download("/admin/hr/export/callbacks.xlsx", "geri-arama.xlsx", { params: f })} className="btn-outline !py-2" data-testid="cb-export-xlsx"><FileSpreadsheet className="h-4 w-4" /> Excel</button>
        <button onClick={() => setAdding(true)} className="btn-red !py-2" data-testid="cb-add-btn"><Plus className="h-4 w-4" /> Manuel Kayıt Ekle</button>
      </AdminTitle>
      <div className="mb-4 grid gap-3 rounded-lg border border-zinc-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="relative"><Search className="absolute left-3 top-3.5 h-4 w-4 text-zinc-400" /><input value={f.q} onChange={set("q")} placeholder="Ad, telefon, kayıt no..." className="field-input !pl-9" data-testid="cb-filter-q" /></div>
        <select value={f.status} onChange={set("status")} className="field-input" data-testid="cb-filter-status"><option value="">Tüm durumlar</option>{CB_STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
        <input type="date" value={f.date_from} onChange={set("date_from")} className="field-input" aria-label="Başlangıç" />
        <input type="date" value={f.date_to} onChange={set("date_to")} className="field-input" aria-label="Bitiş" />
      </div>
      <CallbackTable rows={rows} onOpen={setOpen} />
      <AddDialog open={adding} onClose={() => setAdding(false)} onAdded={load} />
      <Dialog open={!!open} onOpenChange={(o) => !o && close()}>{open && <Detail key={open.id} item={open} onChanged={load} onClose={close} />}</Dialog>
    </div>
  );
}
